import { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@moneyskyline/ui';
import { useAskCommit, AskCommitConflictError, AskCommitExpiredError, AskCommitUnknownOutcomeError, type AskCommitInput, type AskCommitResult } from '@/api/hooks/useAskCommit';
import { useAskDraft, useAskPreview } from '@/api/hooks/useAskPreview';
import type { AskDraft } from '@/api/hooks/askSchemas';
import type { ShellViewportBand } from '@/app/navigation/viewportBand';
import { AskEntry } from './components/AskEntry';
import { AskProposalBody } from './components/AskProposalBody';
import { AskCommitForm } from './components/AskCommitForm';
import { askOverlayReducer, initialAskOverlayState } from './askReducer';
import { AskResultSurface } from './AskResultSurface';
import { AskAnalyticalResult } from './AskAnalyticalResult';

function AskDraftLoader({
  draftId,
  onSuccess,
  onError,
  refetchRef,
}: {
  draftId: string;
  onSuccess: (draft: AskDraft) => void;
  onError: () => void;
  refetchRef: { current: (() => Promise<unknown>) | null };
}) {
  const draftQuery = useAskDraft(draftId);
  const { refetch } = draftQuery;

  useEffect(() => {
    refetchRef.current = () => refetch();
    return () => { refetchRef.current = null; };
  }, [refetch, refetchRef]);

  useEffect(() => {
    if (draftQuery.data) onSuccess(draftQuery.data);
    if (draftQuery.isError) onError();
  }, [draftQuery.data, draftQuery.isError, onError, onSuccess]);

  return null;
}

function scopeFromUrl(params: URLSearchParams) {
  const accountIds = params.getAll('account');
  const scope: { account_id: string[]; date_from?: string; date_to?: string } = { account_id: accountIds };
  const dateFrom = params.get('from');
  const dateTo = params.get('to');
  if (dateFrom && dateTo) { scope.date_from = dateFrom; scope.date_to = dateTo; }
  return scope;
}

function newRequestId(ref: { current: number }) {
  ref.current += 1;
  return ref.current;
}

function focusAskOpener() {
  document.querySelector<HTMLElement>('button[aria-haspopup="dialog"]')?.focus();
}

function visibleStatus(phase: string, message: string | null) {
  const messages: Record<string, string> = {
    'no-match': 'No items matched this request.',
    unsupported: 'This request is not supported yet.',
    'needs-narrower-scope': 'Please narrow the date or account scope and try again.',
    'partial-source': 'Some sources are incomplete. Review the available items before continuing.',
    'changed-since': 'The reviewed items changed. Re-preview before saving.',
    expired: 'This preview expired. Re-preview to continue.',
    failed: message ?? 'We could not complete this request.',
    cancelled: 'Preview cancelled. Nothing was changed.',
    'checking-result': 'Checking whether the request completed…',
  };
  return messages[phase] ?? message ?? 'No result is available.';
}

function AskOverlayContent({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const draftId = searchParams.get('draft');
  const askPreview = useAskPreview();
  const askCommit = useAskCommit();
  const [state, dispatch] = useReducer(askOverlayReducer, { ...initialAskOverlayState, phase: draftId ? 'parsing' : 'entry' });
  const [commitResult, setCommitResult] = useState<AskCommitResult | null>(null);
  const requestIdRef = useRef(0);
  const commitOperationRef = useRef<string | null>(null);
  const draftRefetchRef = useRef<(() => Promise<unknown>) | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const onDraftLoaded = useCallback((draft: AskDraft) => {
    dispatch({ type: 'PREVIEW_SUCCESS', requestId: 0, draft });
  }, []);
  const onDraftLoadError = useCallback(() => {
    dispatch({ type: 'PREVIEW_FAILED', requestId: 0, message: 'We could not reload this preview.' });
  }, []);

  useLayoutEffect(() => {
    function remember(event: FocusEvent) {
      if (event.target instanceof HTMLElement && !event.target.closest('[role="dialog"]')) openerRef.current = event.target;
    }
    document.addEventListener('focusin', remember);
    return () => document.removeEventListener('focusin', remember);
  }, []);

  const focusOpener = useCallback(() => {
    const opener = openerRef.current?.isConnected ? openerRef.current : document.querySelector<HTMLElement>('button[aria-haspopup="dialog"]');
    opener?.focus();
  }, []);

  function preview(sentence: string) {
    const requestId = newRequestId(requestIdRef);
    dispatch({ type: 'PREVIEW_REQUEST', requestId, sentence });
    askPreview.mutate({ sentence, scope: scopeFromUrl(searchParams), prior_draft_id: draftId }, {
      onSuccess: (draft) => {
        dispatch({ type: 'PREVIEW_SUCCESS', requestId, draft });
        setSearchParams((previous) => { const next = new URLSearchParams(previous); next.set('draft', draft.draft_id); return next; });
      },
      onError: (error) => dispatch({ type: 'PREVIEW_FAILED', requestId, message: error.message }),
    });
  }

  function cancelPreview() {
    askPreview.cancel?.();
    dispatch({ type: 'PREVIEW_CANCEL', requestId: requestIdRef.current });
  }

  function commit(fields: Omit<AskCommitInput, 'draft_id' | 'operation_id'>) {
    if (!state.draft) return;
    const operationId = commitOperationRef.current ?? globalThis.crypto?.randomUUID?.();
    if (!operationId) { dispatch({ type: 'COMMIT_FAILED', message: 'We could not create a request identity.' }); return; }
    commitOperationRef.current = operationId;
    dispatch({ type: 'COMMIT_REQUEST' });
    askCommit.mutate({ ...fields, draft_id: state.draft.draft_id, operation_id: operationId }, {
      onSuccess: (result) => { setCommitResult(result); dispatch({ type: 'COMMIT_SUCCESS' }); },
      onError: (error) => {
        if (error instanceof AskCommitConflictError) dispatch({ type: 'COMMIT_CONFLICT', message: error.message });
        else if (error instanceof AskCommitExpiredError) dispatch({ type: 'COMMIT_EXPIRED', message: error.message });
        else if (error instanceof AskCommitUnknownOutcomeError) dispatch({ type: 'COMMIT_UNKNOWN' });
        else dispatch({ type: 'COMMIT_FAILED', message: error.message });
      },
    });
  }

  function close() { onOpenChange(false); focusOpener(); }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onOpenChange(false);
        focusOpener();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusOpener, onOpenChange]);
  const reviewPhase = ['preview-ready', 'likely-related-review', 'partial-source'].includes(state.phase);
  const canContinue = state.draft && (state.acceptedIds.length > 0 || state.draft.supported_outcomes.includes('condition'));

  const content = state.phase === 'entry' || state.phase === 'typing' ? <AskEntry initialSentence={state.sentence} onSubmit={preview} />
    : state.phase === 'parsing' ? <div className="flex items-center gap-2 text-sm" role="status" aria-live="polite"><span aria-hidden="true" className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-border border-t-link" />Reading recent items…<button type="button" className="ml-auto underline" onClick={cancelPreview}>Cancel</button></div>
    : state.phase === 'confirmation' && state.draft ? <AskCommitForm draft={state.draft} acceptedIds={state.acceptedIds} pending={askCommit.isPending} onBack={() => dispatch({ type: 'RESET' })} onSave={commit} />
    : state.phase === 'committed' && commitResult ? <AskResultSurface result={commitResult} />
    : state.phase === 'checking-result' ? <div className="flex flex-col gap-3" role="status" aria-live="polite"><p>{visibleStatus(state.phase, state.message)}</p><button type="button" className="min-h-11 rounded-md border border-input px-3" onClick={() => void draftRefetchRef.current?.()}>Check again</button></div>
    : state.draft && reviewPhase ? <div className="flex flex-col gap-3"><AskProposalBody draft={state.draft} acceptedIds={new Set(state.acceptedIds)} onToggle={(id) => dispatch({ type: 'TOGGLE_ID', id })} />{state.draft.analytical_result && <AskAnalyticalResult result={state.draft.analytical_result} accountIds={state.draft.effective_scope.accounts.map((account) => account.account_id)} dateFrom={state.draft.effective_scope.date_from ?? undefined} dateTo={state.draft.effective_scope.date_to ?? undefined} />}<button type="button" className="min-h-11 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50" disabled={!canContinue} onClick={() => dispatch({ type: 'BEGIN_CONFIRMATION' })}>Continue · {state.acceptedIds.length} item{state.acceptedIds.length === 1 ? '' : 's'}</button></div>
    : <div className="flex flex-col gap-3 p-3" data-testid={`ask-state-${state.phase}`} role="status" aria-live="polite"><p>{visibleStatus(state.phase, state.message)}</p>{['expired', 'changed-since'].includes(state.phase) && <button type="button" className="min-h-11 rounded-md border border-input px-3" onClick={() => preview(state.sentence)}>Re-preview</button>}{['failed', 'cancelled'].includes(state.phase) && <button type="button" className="min-h-11 rounded-md border border-input px-3" onClick={() => dispatch({ type: 'RESET' })}>Back to Ask</button>}</div>;

  const panel = <div className="flex flex-col gap-3"><div className="flex items-center justify-between"><h2 className="text-sm font-semibold">Ask MoneySkyline</h2><button type="button" onClick={close} className="min-h-11 px-2 text-sm text-muted-foreground">Close</button></div>{state.sentence ? <p data-testid="ask-retained-sentence" className="text-sm text-muted-foreground">{state.sentence}</p> : null}{content}</div>;
  const draftLoader = draftId ? <AskDraftLoader draftId={draftId} onSuccess={onDraftLoaded} onError={onDraftLoadError} refetchRef={draftRefetchRef} /> : null;
  return <>{draftLoader}{panel}</>;
}

export function AskOverlay(props: { band: ShellViewportBand; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { band, open, onOpenChange } = props;
  if (band === 'mobile') return <Sheet open={open} onOpenChange={onOpenChange}><SheetContent side="full" aria-describedby="ask-overlay-description" onCloseAutoFocus={(event) => { event.preventDefault(); focusAskOpener(); }}><SheetHeader><SheetTitle>Ask MoneySkyline</SheetTitle><SheetDescription id="ask-overlay-description">Ask a question about your own ledger in plain English.</SheetDescription></SheetHeader><div className="mt-3">{open && <AskOverlayContent onOpenChange={onOpenChange} />}</div></SheetContent></Sheet>;
  if (band === 'tablet') return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-[560px]" aria-describedby="ask-overlay-description" onCloseAutoFocus={(event) => { event.preventDefault(); focusAskOpener(); }}><DialogHeader><DialogTitle>Ask MoneySkyline</DialogTitle><DialogDescription id="ask-overlay-description">Ask a question about your own ledger in plain English.</DialogDescription></DialogHeader>{open && <AskOverlayContent onOpenChange={onOpenChange} />}</DialogContent></Dialog>;
  if (!open) return null;
  return <FocusScope asChild loop trapped onUnmountAutoFocus={(event) => { event.preventDefault(); focusAskOpener(); }}><div role="dialog" aria-modal="false" aria-label="Ask MoneySkyline" data-testid="ask-desktop-panel" className="fixed inset-y-0 right-0 z-40 flex w-[420px] flex-col gap-3 overflow-y-auto border-l border-border bg-background p-4 shadow-overlay"><AskOverlayContent onOpenChange={onOpenChange} /></div></FocusScope>;
}

export default AskOverlay;
